import {Injectable} from "@angular/core";
import {FormGroup, ValidationErrors, ValidatorFn} from "@angular/forms";
import {IReactForm} from "../../shared/interfaces/forms/exercises/IReactForm";
import {IVerticalForm} from "../../shared/interfaces/forms/exercises/IVerticalForm";
import {ICompanionForm} from "../../shared/interfaces/forms/exercises/ICompanionForm";
import {ECompanionMode} from "../../shared/enums/ECompanionMode";
import {ECompanionAgilityType} from "../../shared/enums/ECompanionAgilityType";
import {ECompanionNumberType} from "../../shared/enums/ECompanionNumberType";
import {ECompanionSimultaneousType} from "../../shared/enums/ECompanionSimultaneousType";
import {EStroopEffectType} from "../../shared/enums/EStroopEffectType";
import {EAgilityCustomType} from "../../shared/enums/EAgilityCustomType";

@Injectable({
  providedIn: "root",
})
export class ExerciseValidationService {
  private static selectedCount(values: boolean[] = []): number {
    return values.filter(Boolean).length;
  }

  private static hasErrors(errors: ValidationErrors): ValidationErrors | null {
    return Object.keys(errors).length > 0 ? errors : null;
  }

  static startValueValidation(): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      const requiresValue = group.controls["startType"]?.value === 0;
      return requiresValue && !(group.controls["startValue"]?.value > 0)
        ? {startValueRequired: true}
        : null;
    };
  }

  static neurocognitiveValidation(): ValidatorFn {
    return ((group: FormGroup<IReactForm>): ValidationErrors | null => {
      const errors: ValidationErrors = {};
      const neurocognitiveType = group.controls.neurocognitiveType.value;
      const reactCount = this.selectedCount(group.controls.reactTo.value);
      const dontReactCount = this.selectedCount(group.controls.dontReactTo.value);
      const activeSensorsCount =
        this.selectedCount(group.controls.activeSensors.value) || 5;
      const prompt = group.controls.timerPrompt.value;

      if (neurocognitiveType === 0 && (reactCount !== 1 || dontReactCount !== 0)) {
        errors.invalidNeurocognitiveOff = true;
      }
      if (
        neurocognitiveType === 1 &&
        (reactCount !== 1 || activeSensorsCount < 1 || reactCount + dontReactCount !== activeSensorsCount)
      ) {
        errors.invalidNeurocognitiveArray = true;
      }
      if (
        neurocognitiveType === 2 &&
        (reactCount < 1 ||
          dontReactCount < 1 ||
          prompt == null ||
          prompt === 0)
      ) {
        errors.invalidNeurocognitiveGo = true;
      }
      if (
        neurocognitiveType === 0 &&
        group.controls.stroopEffectType.value !== EStroopEffectType.NONE &&
        prompt === 0
      ) {
        errors.invalidStroopPrompt = true;
      }

      return this.hasErrors(errors);
    }) as ValidatorFn;
  }

  static activeSensorValidation(): ValidatorFn {
    // iOS treats an empty regular React selection as all five sensors and
    // materializes [1, 2, 3, 4, 5] when the configuration is serialized.
    return (() => null) as ValidatorFn;
  }

  static sequenceValidation(): ValidatorFn {
    return ((group: FormGroup): ValidationErrors | null => {
      const sensors: number[] =
        group.controls["sequenceSensors"]?.value ?? [];
      const valid =
        sensors.length >= 1 &&
        sensors.length <= 200 &&
        sensors.every(
          sensor => Number.isInteger(sensor) && sensor >= 1 && sensor <= 7,
        );
      return valid ? null : {invalidSequence: true};
    }) as ValidatorFn;
  }

  static arrowValidation(): ValidatorFn {
    return ((group: FormGroup): ValidationErrors | null => {
      const arrows: number[] = group.controls["agilitySensors"]?.value ?? [];
      const type = group.controls["agilityCustomType"]?.value;

      if (type === EAgilityCustomType.Sequence) {
        return arrows.length >= 1 &&
          arrows.length <= 200 &&
          arrows.every(
            arrow => Number.isInteger(arrow) && arrow >= 1 && arrow <= 9,
          )
          ? null
          : {invalidArrows: true};
      }

      const uniqueArrows = new Set(arrows);
      return arrows.length >= 1 &&
        arrows.length <= 9 &&
        arrows.every(
          arrow => Number.isInteger(arrow) && arrow >= 1 && arrow <= 9,
        ) &&
        uniqueArrows.size === arrows.length
        ? null
        : {invalidActiveArrows: true};
    }) as ValidatorFn;
  }

  static minOneReact(): ValidatorFn {
    return ((group: FormGroup<IVerticalForm>): ValidationErrors | null => {
      if (!group.controls.isVerticalReact.value) {
        return null;
      }
      return this.selectedCount(group.controls.reactTo.value) > 0
        ? null
        : {minReact: true};
    }) as ValidatorFn;
  }

  static companionValidation(): ValidatorFn {
    return ((group: FormGroup<ICompanionForm>): ValidationErrors | null => {
      const errors: ValidationErrors = {};
      const mode = group.controls.companionMode.value;
      const companions = group.controls.numberOfCompanions.value;
      const sensors = group.controls.numberOfSensors.value;
      const agilityType = group.controls.agilityType.value;
      const reactCount = this.selectedCount(group.controls.reactTo.value);
      const dontReactCount = this.selectedCount(group.controls.dontReactTo.value);
      const prompt = group.controls.timerPrompt.value;
      const startValue = group.controls.startValue.value;
      const endValue = group.controls.endValue.value;
      const hasValidNumberRange =
        Number.isInteger(startValue) &&
        startValue >= 1 &&
        startValue <= 999 &&
        Number.isInteger(endValue) &&
        endValue >= 1 &&
        endValue <= 999 &&
        startValue < endValue;
      const validCounts =
        Number.isInteger(companions) &&
        companions >= 1 &&
        companions <= 3 &&
        Number.isInteger(sensors) &&
        sensors >= 1 &&
        sensors <= 5;

      if (mode === ECompanionMode.Simultaneous) {
        if (!validCounts || (companions === 1 && sensors === 1)) {
          errors.invalidCompanionConfiguration = true;
        }
        if (
          group.controls.simultaneousType.value === ECompanionSimultaneousType.Sequence &&
          (group.controls.sequenceSensors.length < 1 ||
            group.controls.sequenceSensors.length > 200 ||
            group.controls.sequenceSensors.value.some(
              sensor =>
                !Number.isInteger(sensor) || sensor < 1 || sensor > 5,
            ))
        ) {
          errors.sequenceRequired = true;
        }
        if (agilityType === ECompanionAgilityType.Color && reactCount < 1) {
          errors.minReactTo = true;
        }
        if (agilityType === ECompanionAgilityType.Color && dontReactCount < 1) {
          errors.minDontReactTo = true;
        }
        if (
          agilityType === ECompanionAgilityType.Number &&
          !hasValidNumberRange
        ) {
          errors.invalidNumberRange = true;
        }
      }

      if (mode === ECompanionMode.React) {
        const neurocognitiveType = group.controls.neurocognitiveType.value;
        if ((neurocognitiveType === 0 || neurocognitiveType === 1) && reactCount !== 1) {
          errors.invalidReactColorCount = true;
        }
        if (neurocognitiveType === 2 && (reactCount < 1 || dontReactCount < 1)) {
          errors.invalidGoNoGoColors = true;
        }
        if (neurocognitiveType === 2 && !(prompt > 0)) {
          errors.invalidPromptValue = true;
        }
        if (
          neurocognitiveType === 0 &&
          group.controls.stroopEffectType.value !== EStroopEffectType.NONE &&
          prompt === 0
        ) {
          errors.invalidPromptValue = true;
        }
        if (
          group.controls.isolatedReact.value &&
          this.selectedCount(group.controls.activeSensors.value) < 1
        ) {
          errors.activeSensorsRequired = true;
        }
      }

      if (mode === ECompanionMode.Agility) {
        const validConfiguration =
          validCounts &&
          ((companions === 1 && sensors >= 2) ||
            (companions === 2 && sensors >= 1));
        if (!validConfiguration) {
          errors.invalidCompanionConfiguration = true;
        }
        if (agilityType === ECompanionAgilityType.Arrow) {
          const arrows = group.controls.arrows.value;
          const isReact = group.controls.arrowsType.value === EAgilityCustomType.React;
          const validArrows =
            arrows.length >= 1 &&
            arrows.length <= (isReact ? 9 : 200) &&
            arrows.every(
              arrow =>
                Number.isInteger(arrow) && arrow >= 1 && arrow <= 9,
            ) &&
            (!isReact || new Set(arrows).size === arrows.length);
          if (!validArrows) {
            errors.invalidCompanionArrows = true;
          }
        }
        if (agilityType === ECompanionAgilityType.Color && reactCount < 1) {
          errors.minReactTo = true;
        }
        if (
          agilityType === ECompanionAgilityType.Number &&
          group.controls.numberType.value === ECompanionNumberType.SingleValue &&
          !hasValidNumberRange
        ) {
          errors.invalidNumberRange = true;
        }
      }

      return this.hasErrors(errors);
    }) as ValidatorFn;
  }

  constructor() {}
}
